import PropTypes from "prop-types";
import React, { useLayoutEffect } from "react";
import ReactDOM from "react-dom";
import "regenerator-runtime/runtime";

import {
  DarkModeToggle,
  MessageProvider,
  PageMessage,
  setPageIdOverride,
  SkipToContent,
  Toolbox,
  Version,
  VisualizeI18n,
} from "@swa-ui/application";
import { AuthResponseDialog } from "@swa-ui/authentication";
import { getBootstrapData } from "@swa-ui/bootstrap";
import { BrowserRouter, window } from "@swa-ui/browser";
import { globalDefines } from "@swa-ui/core/defines";
import {
  BusinessTheme,
  CustomerTheme,
  HelpCenterTheme,
  VacationsTheme,
} from "@swa-ui/design-tokens";
import {
  DotcomApp,
  DotcomMessages,
  DotcomSystemMessages,
  getApiKey,
  getChannelId,
} from "@swa-ui/dotcom";
import { FmDotcomLandingAnalytics, getMktgDataSchemas } from "@swa-ui/dotcom-analytics";
import { EncryptionProvider } from "@swa-ui/encryption";
import { RecentSearchProvider } from "@swa-ui/form";
import { logger } from "@swa-ui/log";
import {
  carBookingFormSchema,
  flightBookingFormSchema,
  SEARCH_TYPE_AIR,
  SEARCH_TYPE_CAR,
} from "@swa-ui/shopping";
import { classNames } from "@swa-ui/string";
import { useColorTheme } from "@swa-ui/theme";
import {
  ALGTransitionBannerPlacement,
  FLIGHT_CAR,
  FLIGHT_HOTEL,
  FLIGHT_HOTEL_CAR,
  getOJTManageMyVacationSchema,
  ManageMyVacationCallToAction,
  ManageMyVacationsFlyout,
  UpdatePassengerInfoCallToAction,
  vacationsBookingFormSchema,
  VacationsPackagesPlacement,
} from "@swa-ui/vacations";

import { bootstrapDependencies } from "../../configure/configureBootstrap";
import { LogDisable } from "../../LogDisable";
import { ThemeModeOverride } from "../../ThemeModeOverride/ThemeModeOverride";
import appOverridesForVacationsTheme from "../../vacations-theme";
import { BookingWidgets } from "../BookingWidgets";
import { LandingPageFooter } from "../LandingPageFooter";
import { LandingPageHeader } from "../LandingPageHeader";
import { LandingPagePlacements } from "../LandingPagePlacements";
import { MboxScripts } from "../MboxScripts";
import { VendorScripts } from "../VendorScripts";
import "./App.module.scss";
import styles from "./App.module.scss";

/**
 * App is responsible for rendering the header, footer, placements and any other forms into any static html page.
 */
const FOOTER = window.document.getElementById("footer");
const PLACEMENT_ELEMENTS = window.document.getElementsByClassName("placement");
const BOOKING_WIDGETS = window.document.getElementById("swa-tabbed-widgets");

const App = (props) => {
  const { pageId, placementParameters = "", themeMode, themeName } = props;
  const { media } = globalDefines;
  const themeSettings = getThemeSettings(themeName);
  const { HeaderTheme, PageTheme } = themeSettings;
  const { addThemeEntries } = useColorTheme();

  useLayoutEffect(() => {
    if (themeName === "vacations") {
      addThemeEntries(appOverridesForVacationsTheme);
    }

    if (pageId) {
      setPageIdOverride(pageId);
    }
  }, []);

  return (
    <BrowserRouter>
      <RecentSearchProvider {...getRecentSearchProviderProps()}>
        <DotcomApp {...getDotcomAppProps()}>
          <LogDisable />
          <ThemeModeOverride themeMode={themeMode} />
          <EncryptionProvider {...getEncryptionProviderProps()}>
            <SkipToContent light={themeName === "vacations"} />
            <FmDotcomLandingAnalytics />
            <MboxScripts />
            <VendorScripts />
            <MessageProvider>
              <PageMessage />
              <VisualizeI18n>
                <Version basePath={"/swa-ui/fm/dotcom-landing-page/v1/"} />
                <div className={getContainerClassName()}>
                  <HeaderTheme>
                    <DarkModeToggle />
                    <LandingPageHeader {...themeSettings} />
                    <DotcomSystemMessages />
                    <DotcomMessages className={styles.messages} />
                  </HeaderTheme>
                </div>
                <PageTheme>
                  <AuthResponseDialog />
                  <Toolbox />
                </PageTheme>
                {PLACEMENT_ELEMENTS && (
                  <LandingPagePlacements {...getLandingPagePlacementsProps()} />
                )}
                {BOOKING_WIDGETS &&
                  ReactDOM.createPortal(
                    <PageTheme>
                      <BookingWidgets />
                    </PageTheme>,
                    BOOKING_WIDGETS
                  )}
                {FOOTER &&
                  ReactDOM.createPortal(
                    <PageTheme>
                      <LandingPageFooter className={getContainerClassName()} />
                    </PageTheme>,
                    FOOTER
                  )}
              </VisualizeI18n>
            </MessageProvider>
          </EncryptionProvider>
        </DotcomApp>
      </RecentSearchProvider>
    </BrowserRouter>
  );

  function getEncryptionProviderProps() {
    return {
      apiKey: getApiKey(),
      channelId: getChannelId(),
      jwtSettings: getBootstrapData("jwt-settings"),
      logger,
      schemas: getOJTManageMyVacationSchema(),
    };
  }

  function getRecentSearchProviderProps() {
    return {
      schemas: {
        [FLIGHT_CAR]: vacationsBookingFormSchema(),
        [FLIGHT_HOTEL]: vacationsBookingFormSchema(),
        [FLIGHT_HOTEL_CAR]: vacationsBookingFormSchema(),
        [SEARCH_TYPE_AIR]: flightBookingFormSchema(),
        [SEARCH_TYPE_CAR]: carBookingFormSchema(),
      },
      storageKey: "dotcom",
      supportedTypes: [
        FLIGHT_CAR,
        FLIGHT_HOTEL,
        FLIGHT_HOTEL_CAR,
        SEARCH_TYPE_AIR,
        SEARCH_TYPE_CAR,
      ],
    };
  }

  function getDotcomAppProps() {
    return {
      additionalContentParameters: { placementParameters },
      appId: "dotcom-landing-page",
      appPlacementComponents: {
        "alg-transition-banner": ALGTransitionBannerPlacement,
        "manage-my-vacations": ManageMyVacationsFlyout,
        packages: VacationsPackagesPlacement,
      },
      basePath: "/dotcom-landing-page/",
      bootstrapDependencies,
      defaultAppSettings: {
        default: {
          carLastBookableDaysRange: 330,
          hotelIFrames: {
            landing: {
              attributes: {
                "data-hash": "_b92facb8b1424",
                "data-size": "100%x100%",
                "data-tpfancy": "false",
                id: "sp_widget",
              },
              source:
                "https://cf.bstatic.com/static/affiliate_base/js/booking_sp_widget.js?checkin=YYYY-MM-DD&checkout=YYYY-MM-DD&label=add_your_label_here&target_aid=2077218&iata_orr=1&iata=IAD&selected_currency=USD&lang=en",
            },
          },
          loginAvailable: true,
          maxPassengersCount: 8,
          mktgExportSchemas: { ...getMktgDataSchemas() },
          searchAvailable: true,
          themeMode,
          themeSelector: false,
        },
      },
      maintenanceCode: "503319996",
      placementsConfig: {
        configuredCommands: {
          SWAV_MANAGE_RESERVATION_ACTION: UpdatePassengerInfoCallToAction,
          SWAV_OJT_MANAGE_VACATION_ACTION: ManageMyVacationCallToAction,
        },
      },
    };
  }

  function getLandingPagePlacementsProps() {
    return {
      PageTheme,
      placements: PLACEMENT_ELEMENTS,
    };
  }

  function getThemeSettings(theme) {
    const themeConfig = {
      business: {
        HeaderTheme: BusinessTheme,
        light: true,
        PageTheme: BusinessTheme,
      },
      customer: {
        HeaderTheme: CustomerTheme,
        PageTheme: CustomerTheme,
      },
      helpcenter: {
        HeaderTheme: HelpCenterTheme,
        light: true,
        menuItemSeparator: "|",
        PageTheme: HelpCenterTheme,
      },
      vacations: {
        HeaderTheme: VacationsTheme,
        light: true,
        menuItemSeparator: "|",
        PageTheme: VacationsTheme,
        uppercaseMenuItems: false,
      },
    };

    return themeConfig[theme];
  }

  function getContainerClassName() {
    return classNames(styles.container, {
      [styles.desktopContainer]: isDesktopOnly(),
    });
  }

  function isDesktopOnly() {
    return media.mediumMax === "0px";
  }
};

App.prototype = {
  /** Override page ID value passed along to the framework. */
  pageId: PropTypes.string,

  /** Additional placement parameters to be passed. */
  placementParameters: PropTypes.string,

  /** The name of the theme to use. From the data-theme-mode HTML attribute. */
  themeMode: PropTypes.string,

  /** The name of the theme to use.  */
  themeName: PropTypes.oneOf(["business", "customer", "helpcenter", "vacations"]),
};

App.defaultProps = {
  themeName: "customer",
};

export { App };
